import React, { useEffect, useState, Children } from 'react';
import { motion } from 'framer-motion';
import { Swords, Shield, Zap, Users, Calendar, ShoppingCart, Lock, Headset, Globe } from 'lucide-react';
type Language = 'es' | 'en' | 'pt';
const translations = {
  es: {
    title: 'Características del',
    titleHighlight: 'Servidor',
    features: [{
      title: 'Economía',
      desc: 'Invertir, farmear o comerciar define tu crecimiento dentro del servidor. No es solo jugar, es saber avanzar estratégicamente.'
    }, {
      title: 'Eventos Personalizados',
      desc: 'Eventos automatizados diarios, invasiones de jefes y guerras exclusivas de fin de semana.'
    }, {
      title: 'PvP Balanceado',
      desc: 'Clases de personajes finamente ajustadas que garantizan combate justo y competitivo.'
    }, {
      title: 'Seguridad Avanzada',
      desc: 'Contamos con la mejor seguridad implementada para proteger tu cuenta y progreso.'
    }, {
      title: 'Innovación',
      desc: 'Somos el primer servidor en Latinoamérica con las implementaciones más avanzadas y el ecosistema más sólido.'
    }, {
      title: 'Mercado Entre Jugadores',
      desc: 'Compra y vende items directamente con otros jugadores de forma segura.'
    }, {
      title: 'Sistema Anti-Trampas',
      desc: 'Protección premium contra hacks y exploits para una economía justa.'
    }, {
      title: 'Soporte 24/7',
      desc: 'Equipo de staff dedicado disponible las 24 horas a través de Discord.'
    }, {
      title: 'Multiplataforma',
      desc: 'Cliente optimizado que funciona sin problemas en sistemas modernos y antiguos.'
    }]
  },
  en: {
    title: 'Server',
    titleHighlight: 'Features',
    features: [{
      title: 'Economy',
      desc: "Invest, farm, or trade to define your growth within the server. It's not just playing, it's knowing how to advance strategically."
    }, {
      title: 'Custom Events',
      desc: 'Daily automated events, boss invasions, and exclusive weekend wars.'
    }, {
      title: 'Balanced PvP',
      desc: 'Finely tuned character classes ensuring fair and competitive combat.'
    }, {
      title: 'Advanced Security',
      desc: 'We have the best security implemented to protect your account and progress.'
    }, {
      title: 'Innovation',
      desc: 'We are the first server in Latin America with the most advanced implementations and the most solid ecosystem.'
    }, {
      title: 'Player Market',
      desc: 'Buy and sell items directly with other players securely.'
    }, {
      title: 'Anti-Cheat System',
      desc: 'Premium protection against hacks and exploits for a fair economy.'
    }, {
      title: '24/7 Support',
      desc: 'Dedicated staff team available around the clock via Discord.'
    }, {
      title: 'Cross-Platform',
      desc: 'Optimized client running smoothly on modern and older systems.'
    }]
  },
  pt: {
    title: 'Características do',
    titleHighlight: 'Servidor',
    features: [{
      title: 'Economia',
      desc: 'Investir, farmar ou comercializar define seu crescimento dentro do servidor. Não é apenas jogar, é saber avançar estrategicamente.'
    }, {
      title: 'Eventos Personalizados',
      desc: 'Eventos automatizados diários, invasões de chefes e guerras exclusivas de fim de semana.'
    }, {
      title: 'PvP Balanceado',
      desc: 'Classes de personagens finamente ajustadas garantindo combate justo e competitivo.'
    }, {
      title: 'Segurança Avançada',
      desc: 'Temos a melhor segurança implementada para proteger sua conta e progresso.'
    }, {
      title: 'Inovação',
      desc: 'Somos o primeiro servidor na América Latina com as implementações mais avançadas e o ecossistema mais sólido.'
    }, {
      title: 'Mercado Entre Jogadores',
      desc: 'Compre e venda itens diretamente com outros jogadores com segurança.'
    }, {
      title: 'Sistema Anti-Trapaça',
      desc: 'Proteção premium contra hacks e exploits para uma economia justa.'
    }, {
      title: 'Suporte 24/7',
      desc: 'Equipe dedicada disponível 24 horas por dia via Discord.'
    }, {
      title: 'Multiplataforma',
      desc: 'Cliente otimizado funcionando perfeitamente em sistemas modernos e antigos.'
    }]
  }
};
const icons = [Zap, Calendar, Swords, Users, Globe, ShoppingCart, Lock, Headset, Shield];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
export function Features() {
  const [language, setLanguage] = useState<Language>('es');
  const t = translations[language];
  useEffect(() => {
    const savedLang = localStorage.getItem('language') as Language;
    if (savedLang) setLanguage(savedLang);
    const handleLanguageChange = (e: CustomEvent<Language>) => {
      setLanguage(e.detail);
    };
    window.addEventListener('languageChange', handleLanguageChange as EventListener);
    return () => window.removeEventListener('languageChange', handleLanguageChange as EventListener);
  }, []);
  return <section id="features" className="pt-24 pb-24 sm:pt-28 sm:pb-28 md:pt-36 md:pb-32 lg:pt-40 lg:pb-36 relative z-10 overflow-hidden" data-id="element-89">
      {/* Lorencia Background */}
      <div className="absolute inset-0 z-0" data-id="element-90">
        <img src="https://cdn.magicpatterns.com/uploads/5o3shCna3mksG95kwFN8rQ/ChatGPT_Image_Mar_27,_2026,_11_44_40_AM.png" alt="Lorencia" className="w-full h-full object-cover object-top" data-id="element-91" />
        <div className="absolute inset-0 bg-gradient-to-b from-darkerBlue/60 via-darkerBlue/50 to-darkerBlue/70" data-id="element-92" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-93">
        <div className="text-center mb-4 sm:mb-6 md:mb-8" data-id="element-94">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4" data-id="element-95">
            {t.title}{' '}
            <span className="text-gradient-gold" data-id="element-96">{t.titleHighlight}</span>
          </motion.h2>
          <motion.div initial={{
          opacity: 0,
          scale: 0
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="w-16 sm:w-24 h-1 bg-gold mx-auto rounded-full" data-id="element-97" />
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} className="grid grid-cols-3 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6" data-id="element-98">
          {t.features.slice(0, 6).map((feature, index) => {
          const Icon = icons[index];
          return <motion.div key={index} variants={itemVariants} className="bg-darkerBlue/30 backdrop-blur-[2px] border border-white/10 p-3 sm:p-4 md:p-5 rounded-lg sm:rounded-xl group hover:border-gold/50 hover:bg-darkerBlue/45 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_8px_25px_rgba(212,175,55,0.1)] relative overflow-hidden text-center" data-id="element-99">
                <div className="absolute inset-0 bg-gradient-to-br from-gold/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-100" />

                <div className="relative z-10 flex flex-col items-center" data-id="element-101">
                  <div className="w-8 h-8 sm:w-9 sm:h-9 md:w-11 md:h-11 rounded-lg bg-darkBlue/60 border border-white/10 flex items-center justify-center mb-2 sm:mb-3 group-hover:border-gold/50 group-hover:shadow-[0_0_15px_rgba(212,175,55,0.3)] transition-all duration-500" data-id="element-102">
                    <Icon className="w-4 h-4 sm:w-4 sm:h-4 md:w-5 md:h-5 text-glowBlue group-hover:text-gold transition-colors duration-500" data-id="element-103" />
                  </div>
                  <h3 className="text-xs sm:text-sm md:text-base font-bold mb-1 sm:mb-1.5 text-white group-hover:text-gold-light transition-colors duration-300 leading-tight" data-id="element-104">
                    {feature.title}
                  </h3>
                  <p className="text-[10px] sm:text-xs md:text-sm text-gray-400 leading-snug sm:leading-relaxed hidden sm:block" data-id="element-105">
                    {feature.desc}
                  </p>
                </div>
              </motion.div>;
        })}
        </motion.div>
      </div>
    </section>;
}